import React, { useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, CircularProgress } from '@mui/material';
import {
  GridOverlay,
  type GridRenderCellParams,
  type GridValueFormatterParams,
} from '@mui/x-data-grid';
import {
  CentsDataGridServer,
  DEFAULT_PAGE_SIZE_OPTIONS,
} from 'components/common/dataGrid/dataGridServer';
import { formatWashboardTimestamp } from 'utils/formatTimestamp';
import { type DeviceActivityLog, DeviceActivityLogType } from 'api/admin/washboard/washboard';
import { useWashboardDeviceActivityLogs } from 'api/queries/useWashboardDeviceActivityLogs';
import './DeviceActivityLogs.scss';

const DEFAULT_PAGE_SIZE = 20;

const ACTIVITY_LOG_TYPE_LABELS: Record<DeviceActivityLogType, string> = {
  [DeviceActivityLogType.FIRMWARE_UPDATE]: 'Firmware Update',
  [DeviceActivityLogType.ENV_VARS_UPDATE]: 'Environment Variables Update',
  [DeviceActivityLogType.LOCATION_UPDATE]: 'Location Update',
  [DeviceActivityLogType.REBOOT]: 'Reboot',
};

const COLUMNS = [
  {
    headerName: 'Date & Time',
    field: 'createdAt',
    flex: 0.9,
    sortable: false,
    valueFormatter: ({ value }: GridValueFormatterParams<string>) =>
      formatWashboardTimestamp(value),
  },
  {
    headerName: 'Activity',
    field: 'type',
    flex: 0.8,
    sortable: false,
    valueFormatter: ({ value }: GridValueFormatterParams<DeviceActivityLogType>) =>
      ACTIVITY_LOG_TYPE_LABELS[value] ?? value,
  },
  {
    headerName: 'Details',
    field: 'text',
    flex: 2,
    sortable: false,
    renderCell: (params: GridRenderCellParams<DeviceActivityLog, string | null>) => (
      <span className="activity-log-details" title={params.value ?? undefined}>
        {params.value || 'N/A'}
      </span>
    ),
  },
];

const NoActivityLogsOverlay = () => (
  <GridOverlay>
    <span className="activity-logs-empty">No activity logs for this device</span>
  </GridOverlay>
);

const GRID_COMPONENTS = { NoRowsOverlay: NoActivityLogsOverlay };

type DeviceActivityLogsProps = {
  deviceId: number;
};

const DeviceActivityLogs: React.FC<DeviceActivityLogsProps> = ({ deviceId }) => {
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isLoading, isFetching, isError, error, refetch } = useWashboardDeviceActivityLogs({
    deviceId,
    page: pageNumber + 1,
    limit: pageSize,
  });

  const activityLogs = data?.activityLogs ?? [];
  const total = data?.total ?? 0;

  const handlePageChange = ({ page }: { page: number }) => {
    setPageNumber(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (!Number.isInteger(newPageSize) || newPageSize <= 0 || newPageSize === pageSize) {
      return;
    }
    setPageNumber(0);
    setPageSize(newPageSize);
  };

  const requestError = isError
    ? error?.response?.data?.error ?? 'Failed to load device activity logs'
    : null;

  return (
    <div className="device-activity-logs">
      <div className="activity-logs-toolbar">
        <Button
          variant="outlined"
          startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => refetch()}
          disabled={isFetching}
        >
          Refresh
        </Button>
      </div>

      {requestError ? (
        <div className="activity-logs-error" role="alert">
          {requestError}
        </div>
      ) : (
        <CentsDataGridServer
          tableName="WASHBOARD_DEVICE_ACTIVITY_LOGS"
          data={activityLogs}
          columns={COLUMNS}
          isLoading={isLoading}
          rowCount={total}
          page={pageNumber}
          pageSize={pageSize}
          setPageSize={handlePageSizeChange}
          pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
          onPageChange={handlePageChange}
          components={GRID_COMPONENTS}
        />
      )}
    </div>
  );
};

export default DeviceActivityLogs;
